import { PermissionKey } from 'src/types/permissions.types';

export interface PermissionInfo {
  key: PermissionKey;
  label: string;
  description: string;
}

export interface PermissionGroup {
  resource: string;
  title: string;
  description: string;
  /** Iconify icon name, same set the Cohorts screens use. */
  icon: string;
  color: string;
  permissions: PermissionInfo[];
}

/**
 * Plain-language names for every permission, grouped the way the Roles
 * screens show them. Every key in PERMISSION_KEYS except '*' must appear here.
 */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    resource: 'students',
    title: 'Scholars',
    description: 'Scholar profiles and rosters',
    icon: 'solar:user-id-bold-duotone',
    color: '#2563EB',
    permissions: [
      { key: 'students.view_all', label: 'See every scholar', description: 'All scholars across the school' },
      { key: 'students.view_assigned', label: 'See their scholars', description: 'Only scholars in their own cohorts' },
      { key: 'students.view_self', label: 'See own profile', description: 'Only their own scholar profile' },
      { key: 'students.create', label: 'Add scholars', description: 'Create new scholar accounts' },
      { key: 'students.edit', label: 'Edit scholars', description: 'Change scholar details' },
      { key: 'students.delete', label: 'Remove scholars', description: 'Deactivate scholar accounts' },
      { key: 'students.manage', label: 'Manage scholars', description: 'Everyday roster management' },
    ],
  },
  {
    resource: 'cohorts',
    title: 'Cohorts',
    description: 'Class sections such as 6A or 7B',
    icon: 'solar:users-group-two-rounded-bold-duotone',
    color: '#7C3AED',
    permissions: [
      { key: 'cohorts.view_all', label: 'See every cohort', description: 'All sections across the school' },
      { key: 'cohorts.view_assigned', label: 'See their cohorts', description: 'Only sections they are part of' },
      { key: 'cohorts.create', label: 'Create cohorts', description: 'Add new sections' },
      { key: 'cohorts.edit', label: 'Edit cohorts', description: 'Rename sections or move them between grades' },
      { key: 'cohorts.delete', label: 'Delete cohorts', description: 'Remove sections; balances stay safe' },
      { key: 'cohorts.manage_teachers', label: 'Assign educators', description: 'Attach teachers to sections' },
      { key: 'cohorts.manage_students', label: 'Enroll scholars', description: 'Move scholars in and out of sections' },
    ],
  },
  {
    resource: 'classes',
    title: 'Grade Levels',
    description: 'Grade tiers that hold cohorts',
    icon: 'solar:diploma-verified-bold-duotone',
    color: '#0891B2',
    permissions: [
      { key: 'classes.view_all', label: 'See grade levels', description: 'All grade tiers' },
      { key: 'classes.create', label: 'Add grade levels', description: 'Create new grade tiers' },
      { key: 'classes.edit', label: 'Edit grade levels', description: 'Rename grade tiers' },
      { key: 'classes.delete', label: 'Delete grade levels', description: 'Remove grade tiers' },
    ],
  },
  {
    resource: 'teachers',
    title: 'Educators',
    description: 'Teacher accounts',
    icon: 'solar:case-round-minimalistic-bold-duotone',
    color: '#D97706',
    permissions: [
      { key: 'teachers.view_all', label: 'See educators', description: 'The teacher directory' },
      { key: 'teachers.manage', label: 'Manage educators', description: 'Create and edit teacher accounts' },
    ],
  },
  {
    resource: 'transactions',
    title: 'Street Cents & Banking',
    description: 'Payments, charges and fees',
    icon: 'solar:wallet-money-bold-duotone',
    color: '#059669',
    permissions: [
      { key: 'transactions.view_all', label: 'See every transaction', description: 'The whole school ledger' },
      { key: 'transactions.view_assigned', label: 'See their transactions', description: 'Ledger for their scholars' },
      { key: 'transactions.view_self', label: 'See own transactions', description: 'Only their own ledger' },
      { key: 'transactions.create', label: 'Pay and charge', description: 'Pay scholars, apply charges and fees' },
      { key: 'transaction_types.manage', label: 'Manage transaction types', description: 'Define jobs, charges and fees' },
      { key: 'loans.approve', label: 'Approve loans', description: 'Approve or deny loan requests' },
      { key: 'payroll.access', label: 'Run payroll', description: 'Grade imports and direct deposits' },
    ],
  },
  {
    resource: 'whammies',
    title: 'Whammies',
    description: 'Financial scenarios and challenges',
    icon: 'solar:bolt-circle-bold-duotone',
    color: '#DB2777',
    permissions: [
      { key: 'whammies.view_all', label: 'See every whammy', description: 'All scenarios and results' },
      { key: 'whammies.view_assigned', label: 'See assigned whammies', description: 'Scenarios for their scholars' },
      { key: 'whammies.create', label: 'Create whammies', description: 'Write new scenarios' },
      { key: 'whammies.edit', label: 'Edit whammies', description: 'Change existing scenarios' },
      { key: 'whammies.delete', label: 'Delete whammies', description: 'Remove scenarios' },
      { key: 'whammies.assign', label: 'Assign whammies', description: 'Give scenarios to scholars' },
    ],
  },
  {
    resource: 'street_cred',
    title: 'S.T.R.E.E.T Cred',
    description: 'Behavior points',
    icon: 'solar:medal-star-bold-duotone',
    color: '#EA580C',
    permissions: [
      { key: 'street_cred.view_all', label: 'See all behavior', description: 'Every scholar’s behavior log' },
      { key: 'street_cred.view_assigned', label: 'See their scholars’ behavior', description: 'Behavior for their scholars' },
      { key: 'street_cred.view_self', label: 'See own behavior', description: 'Only their own log' },
      { key: 'street_cred.manage', label: 'Award and deduct', description: 'Record behavior and manage types' },
    ],
  },
  {
    resource: 'roles',
    title: 'Administration',
    description: 'Who can do what',
    icon: 'solar:shield-user-bold-duotone',
    color: '#475569',
    permissions: [
      { key: 'roles.manage', label: 'Manage roles', description: 'Create roles, change permissions, assign people' },
    ],
  },
];

export const ALL_PERMISSIONS: PermissionInfo[] = PERMISSION_GROUPS.flatMap((group) => group.permissions);

/** Shells a role can land in, with the words the Roles screens use for them. */
export const ROLE_SHELLS = [
  {
    value: '/admin',
    label: 'Admin dashboard',
    description: 'Whole-school view, for staff such as a Dean',
    icon: 'solar:buildings-2-bold-duotone',
  },
  {
    value: '/teacher',
    label: 'Teacher dashboard',
    description: 'Works inside assigned cohorts, like a teacher or banker',
    icon: 'solar:case-round-minimalistic-bold-duotone',
  },
  {
    value: '/student',
    label: 'Scholar dashboard',
    description: 'Sees and acts on their own profile only',
    icon: 'solar:user-id-bold-duotone',
  },
] as const;

export function getShellLabel(shell: string): string {
  return ROLE_SHELLS.find((s) => s.value === shell)?.label || shell;
}
